import gsap from 'gsap';
import { Draggable } from 'gsap/all';

gsap.registerPlugin(Draggable);

export default function initTimeline() {
    const timelines = document.querySelectorAll('.timeline');

    timelines.forEach(timeline => {
        const wrapper = timeline.querySelector('.timeline__wrapper');
        const prevBtn = timeline.querySelector('.swiper-button-prev');
        const nextBtn = timeline.querySelector('.swiper-button-next');
        const steps = timeline.querySelectorAll('.step__button');
        const dates = timeline.querySelectorAll('.timeline__step');
        const head = timeline.querySelector('.timeline__head');
        const stepsWrapper = timeline.querySelector('.steps__wrapper');
        const stepsContainer = timeline.querySelector('.steps');

        if (!wrapper) return;

        // Ensure offsetParent for dates is the wrapper
        wrapper.style.position = 'relative';

        let currentX = 0;

        const getMaxScroll = () => {
            // The wrapper's total scrollable width minus visible container width
            let maxScroll = -(wrapper.scrollWidth - timeline.clientWidth);
            return maxScroll > 0 ? 0 : maxScroll;
        };

        const getStepsMaxScroll = () => {
            if (!stepsWrapper || !stepsContainer) return 0;
            let maxScroll = -(stepsWrapper.scrollWidth - stepsContainer.clientWidth);
            return maxScroll > 0 ? 0 : maxScroll;
        };

        const goToX = (x) => {
            const maxScroll = getMaxScroll();

            // Clamp x between maxScroll and 0
            if (x > 0) x = 0;
            if (x < maxScroll) x = maxScroll;

            currentX = x;

            gsap.to(wrapper, {
                x: currentX,
                duration: 0.8,
                ease: 'power3.inOut'
            });

            if (head && window.innerWidth > 991) {
                gsap.to(head, {
                    autoAlpha: currentX === 0 ? 1 : 0,
                    duration: 0.8,
                    ease: 'power3.inOut'
                });
            }

            if (stepsWrapper) {
                const stepsMaxScroll = getStepsMaxScroll();
                let progress = 0;
                if (maxScroll < 0) {
                    progress = currentX / maxScroll;
                }
                const stepsX = progress * stepsMaxScroll;

                gsap.to(stepsWrapper, {
                    x: stepsX,
                    duration: 0.8,
                    ease: 'power3.inOut'
                });
            }

            updateStepsStates(currentX);
        };

        let draggableInstance = null;
        let stepsDraggableInstance = null;

        const initDraggable = () => {
            if (window.innerWidth <= 991) {
                // Wrapper Draggable
                if (!draggableInstance) {
                    draggableInstance = Draggable.create(wrapper, {
                        type: 'x',
                        edgeResistance: 0.65,
                        bounds: {
                            minX: getMaxScroll(),
                            maxX: 0
                        },
                        inertia: true,
                        onDrag: function () {
                            currentX = this.x;
                            if (stepsWrapper) {
                                const stepsMaxScroll = getStepsMaxScroll();
                                let progress = 0;
                                if (getMaxScroll() < 0) {
                                    progress = currentX / getMaxScroll();
                                }
                                gsap.set(stepsWrapper, { x: progress * stepsMaxScroll });
                                // Keep steps draggable position in sync so it doesn't jump
                                if (stepsDraggableInstance) {
                                    stepsDraggableInstance.update();
                                }
                            }
                            updateStepsStates(currentX);
                        },
                        onThrowUpdate: function () {
                            currentX = this.x;
                            if (stepsWrapper) {
                                const stepsMaxScroll = getStepsMaxScroll();
                                let progress = 0;
                                if (getMaxScroll() < 0) {
                                    progress = currentX / getMaxScroll();
                                }
                                gsap.set(stepsWrapper, { x: progress * stepsMaxScroll });
                                if (stepsDraggableInstance) {
                                    stepsDraggableInstance.update();
                                }
                            }
                            updateStepsStates(currentX);
                        }
                    })[0];
                } else {
                    draggableInstance.enable();
                    draggableInstance.applyBounds({
                        minX: getMaxScroll(),
                        maxX: 0
                    });
                }

                // Steps Draggable
                if (stepsWrapper && stepsContainer) {
                    if (!stepsDraggableInstance) {
                        stepsDraggableInstance = Draggable.create(stepsWrapper, {
                            type: 'x',
                            edgeResistance: 0.65,
                            bounds: {
                                minX: getStepsMaxScroll(),
                                maxX: 0
                            },
                            inertia: true,
                            onDrag: function () {
                                const stepsX = this.x;
                                const stepsMaxScroll = getStepsMaxScroll();
                                let progress = 0;
                                if (stepsMaxScroll < 0) {
                                    progress = stepsX / stepsMaxScroll;
                                }

                                currentX = progress * getMaxScroll();
                                gsap.set(wrapper, { x: currentX });
                                if (draggableInstance) {
                                    draggableInstance.update();
                                }
                                updateStepsStates(currentX);
                            },
                            onThrowUpdate: function () {
                                const stepsX = this.x;
                                const stepsMaxScroll = getStepsMaxScroll();
                                let progress = 0;
                                if (stepsMaxScroll < 0) {
                                    progress = stepsX / stepsMaxScroll;
                                }

                                currentX = progress * getMaxScroll();
                                gsap.set(wrapper, { x: currentX });
                                if (draggableInstance) {
                                    draggableInstance.update();
                                }
                                updateStepsStates(currentX);
                            }
                        })[0];
                    } else {
                        stepsDraggableInstance.enable();
                        stepsDraggableInstance.applyBounds({
                            minX: getStepsMaxScroll(),
                            maxX: 0
                        });
                    }
                }
            } else {
                if (draggableInstance) {
                    draggableInstance.disable();
                }
                if (stepsDraggableInstance) {
                    stepsDraggableInstance.disable();
                }
            }
        };

        const getActiveIndex = (x) => {
            let activeIdx = 0;
            dates.forEach((date, index) => {
                if (-date.offsetLeft >= x - (window.innerWidth / 3)) {
                    activeIdx = index;
                }
            });
            return activeIdx;
        };

        // Update states on slide change
        const updateStepsStates = (x) => {
            steps.forEach(s => {
                s.classList.remove('is-active');
                s.setAttribute('aria-pressed', 'false');
            });

            const activeIdx = getActiveIndex(x);

            if (steps[activeIdx]) {
                steps[activeIdx].classList.add('is-active');
                steps[activeIdx].setAttribute('aria-pressed', 'true');
            }
        };

        if (prevBtn) {
            prevBtn.addEventListener('click', () => {
                if (window.innerWidth <= 991) {
                    const idx = getActiveIndex(currentX);
                    const prevIdx = Math.max(0, idx - 1);
                    if (dates[prevIdx]) {
                        goToX(-dates[prevIdx].offsetLeft);
                    }
                } else {
                    const vw = .8 * window.innerWidth;
                    goToX(currentX + vw);
                }
            });
        }

        if (nextBtn) {
            nextBtn.addEventListener('click', () => {
                if (window.innerWidth <= 991) {
                    const idx = getActiveIndex(currentX);
                    const nextIdx = Math.min(dates.length - 1, idx + 1);
                    if (dates[nextIdx]) {
                        goToX(-dates[nextIdx].offsetLeft);
                    }
                } else {
                    const vw = .8 * window.innerWidth;
                    goToX(currentX - vw);
                }
            });
        }

        steps.forEach((step, index) => {
            step.addEventListener('click', () => {
                if (dates[index]) {
                    // Go to the offsetLeft of the corresponding date element
                    const targetX = -dates[index].offsetLeft;
                    goToX(targetX);
                }
            });
        });

        // Handle window resize to re-calculate boundaries
        window.addEventListener('resize', () => {
            if (window.innerWidth > 991) {
                goToX(currentX);
            }
            initDraggable();
        }, { passive: true });

        // Initial state setup
        requestAnimationFrame(() => {
            goToX(0);
            initDraggable();
        });
    });
}
